import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { scenariosAPI, chatAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const ScenariosPage = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      const data = await scenariosAPI.getAll();
      setScenarios(data);
    } catch (error) {
      toast.error('Falha ao carregar cenários');
    } finally {
      setLoading(false);
    }
  };

  const startLesson = (scenarioId) => {
    navigate(`/guided-lesson/${scenarioId}`);
  };

  const startPractice = async (scenarioId) => {
    try {
      const conversation = await chatAPI.createConversation('scenario', scenarioId);
      navigate(`/chat/${conversation.id}?guided=true`);
    } catch (error) {
      toast.error('Falha ao iniciar prática');
    }
  };

  const scenarioImages = {
    restaurant: 'https://images.pexels.com/photos/29489315/pexels-photo-29489315.jpeg?auto=compress&cs=tinysrgb&w=600',
    job_interview: 'https://images.unsplash.com/photo-1637665662134-db459c1bbb46?w=600&h=400&fit=crop',
    airport: 'https://images.unsplash.com/photo-1773734874748-8e7893de8e6f?w=600&h=400&fit=crop',
    shopping: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop',
    doctor: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=600&h=400&fit=crop',
    hotel: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop',
    gym: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop',
    casual: 'https://images.unsplash.com/photo-1543807535-eceef0bc6599?w=600&h=400&fit=crop',
    bank: 'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=600&h=400&fit=crop',
    pharmacy: 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=600&h=400&fit=crop',
    supermarket: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=600&h=400&fit=crop',
    directions: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600&h=400&fit=crop',
    emergency: 'https://images.unsplash.com/photo-1587745416684-47953f16f02f?w=600&h=400&fit=crop',
    coffee_shop: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=600&h=400&fit=crop',
  };

  const difficultyLabels = {
    beginner: { label: 'Iniciante', color: 'bg-lime text-forest' },
    intermediate: { label: 'Intermediário', color: 'bg-clay text-white' },
    advanced: { label: 'Avançado', color: 'bg-forest text-sand' },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-forest/20 border-t-forest rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="scenarios-page">
      <header className="bg-white border-b border-forest/10 p-4">
        <div className="container mx-auto flex items-center gap-4">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-fraunces text-xl font-semibold text-forest">Lições por Cenário</h1>
            <p className="text-xs text-stone">Aprenda frases úteis para situações reais</p>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((scenario, index) => (
            <motion.div
              key={scenario.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-soft"
            >
              <div className="aspect-[16/9] overflow-hidden relative">
                <img
                  src={scenarioImages[scenario.id] || scenarioImages.restaurant}
                  alt={scenario.title}
                  className="w-full h-full object-cover"
                />
                <span className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${difficultyLabels[scenario.difficulty]?.color || 'bg-lime text-forest'}`}>
                  {difficultyLabels[scenario.difficulty]?.label || scenario.difficulty}
                </span>
              </div>
              
              <div className="p-4">
                <h3 className="font-semibold text-forest text-lg mb-1">{scenario.title}</h3>
                <p className="text-stone text-sm mb-4 line-clamp-2">{scenario.description}</p>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => startLesson(scenario.id)}
                    variant="outline"
                    className="flex-1 rounded-full text-sm py-2"
                    data-testid={`lesson-btn-${scenario.id}`}
                  >
                    <BookOpen className="w-4 h-4 mr-1" />
                    Aprender
                  </Button>
                  <Button
                    onClick={() => startPractice(scenario.id)}
                    className="flex-1 bg-forest text-sand rounded-full text-sm py-2"
                    data-testid={`practice-btn-${scenario.id}`}
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    Praticar
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScenariosPage;